// AuditTrail — small, unobtrusive footer under each assistant answer.
// Shows the brains used (with element-cell glyphs), authors, and optional
// power-mode metrics. Designed to be present but not in the way.

import type { Brain } from '../types';

interface Props {
  brains: Brain[];
  costCents?: number;
  durationMs?: number;
}

export function AuditTrail({ brains, costCents, durationMs }: Props) {
  if (brains.length === 0) return null;
  const authors = Array.from(new Set(brains.map((b) => b.author))).length;

  return (
    <div className="mt-2 inline-flex items-center gap-2 flex-wrap text-[11px] text-text3">
      <span className="text-text2 font-medium">Sources</span>
      <span className="text-border">·</span>
      <div className="inline-flex items-center gap-1.5 flex-wrap">
        {brains.map((b) => (
          <span
            key={b.id}
            title={`${b.name} — ${b.org} · v${b.version} · ${b.author}`}
            className="inline-flex items-center gap-1 bg-card border border-hairline rounded-full pl-1 pr-2 py-0.5 hover:border-ink/30 transition-colors cursor-default"
          >
            <span className="font-mono text-[9px] text-text2 leading-none px-1 py-0.5 bg-bg/40 rounded-sm">
              {b.symbol}·{b.atomic_number}
            </span>
            <span className="text-text2 truncate max-w-[140px]">{b.name}</span>
          </span>
        ))}
      </div>
      <span className="text-border">·</span>
      <span className="font-mono text-[10px] text-text3">
        {brains.length} brain{brains.length === 1 ? '' : 's'} · {authors} author{authors === 1 ? '' : 's'}
        {costCents !== undefined && ` · ${costCents.toFixed(3)}¢`}
        {durationMs !== undefined && ` · ${(durationMs / 1000).toFixed(1)}s`}
      </span>
    </div>
  );
}
